import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@itaas/core/auth/useAuth";
import { PatientFormModal } from "@/components/patients/PatientFormModal";
import { PageHeader } from "@/components/PageHeader";
import { TableSkeleton } from "@/components/skeleton/TableSkeleton";
import { TablePagination } from "@/components/TablePagination";
import { useClientPagination } from "@/hooks/useClientPagination";
import {
  createPatient,
  listPatients,
  patientDetailPath,
  searchPatients,
  syncPatientsFromApi,
} from "@/core/patients/patient-store";
import { patientFullName, patientGenderLabel, patientTypeLabel } from "@/core/patients/types";
import { canWritePatients } from "@/core/permissions";
import { routes } from "@/router/all_routes";

export function PatientsListPage() {
  const auth = useAuth();
  const canWrite = canWritePatients(auth);
  const [query, setQuery] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setSyncing(true);
    void syncPatientsFromApi().finally(() => {
      if (!cancelled) {
        setSyncing(false);
        setRefreshKey((k) => k + 1);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const patients = useMemo(() => {
    void refreshKey;
    return query.trim() ? searchPatients(query) : listPatients();
  }, [query, refreshKey]);

  const pagination = useClientPagination(patients, [query], 25);
  const { pagedItems } = pagination;

  return (
    <div>
      <PageHeader
        title="Liste patients"
        description="Registre des patients de l'établissement — recherche et création."
        actions={
          canWrite ? (
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => setModalOpen(true)}
            >
              Nouveau patient
            </button>
          ) : undefined
        }
      />

      <div className="ph-invoice-sheet mb-4">
        <label className="form-label small fw-semibold mb-1" htmlFor="pat-search">
          Rechercher
        </label>
        <input
          id="pat-search"
          type="search"
          className="form-control form-control-sm w-100"
          style={{ maxWidth: "24rem" }}
          placeholder="Nom, n° dossier, téléphone…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <p className="small text-muted mb-0 mt-2">
          {patients.length} patient{patients.length !== 1 ? "s" : ""}
          {syncing ? " · synchronisation…" : ""}
        </p>
      </div>

      <div className="ph-invoice-sheet">
        {syncing && patients.length === 0 ? (
          <TableSkeleton rows={8} columns={6} />
        ) : patients.length === 0 ? (
          <p className="text-muted mb-0">Aucun patient trouvé.</p>
        ) : (
          <div className="table-responsive ph-diag-table">
            <table className="table table-sm ph-dash-table align-middle mb-0">
              <thead>
                <tr>
                  <th>N° dossier</th>
                  <th>Nom</th>
                  <th>Genre</th>
                  <th>Type</th>
                  <th>Téléphone</th>
                  <th>Adresse</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagedItems.map((patient) => (
                  <tr key={patient.id} className="ph-diag-row">
                    <td data-label="N° dossier">
                      <span className="ph-inv-badge">{patient.fileNumber}</span>
                    </td>
                    <td data-label="Nom" className="fw-medium">
                      {patientFullName(patient)}
                    </td>
                    <td data-label="Genre" className="small">
                      {patientGenderLabel(patient.gender)}
                    </td>
                    <td data-label="Type" className="small">
                      <span className="ph-inv-badge">{patientTypeLabel(patient.patientType)}</span>
                    </td>
                    <td data-label="Téléphone" className="small">
                      {patient.phone || "—"}
                    </td>
                    <td data-label="Adresse" className="small text-muted">
                      {patient.address || "—"}
                    </td>
                    <td data-label="Actions" className="text-end">
                      <Link
                        to={patientDetailPath(patient.id)}
                        className="btn btn-link btn-sm p-0 me-2"
                      >
                        Fiche
                      </Link>
                      <Link
                        to={`${routes.pharmacyInvoice}?patientId=${patient.id}`}
                        className="btn btn-link btn-sm p-0"
                      >
                        Facturer
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <TablePagination
              page={pagination.page}
              pageSize={pagination.pageSize}
              totalItems={pagination.totalItems}
              onPageChange={pagination.setPage}
              onPageSizeChange={pagination.setPageSize}
            />
          </div>
        )}
      </div>

      <PatientFormModal
        open={modalOpen && canWrite}
        onClose={() => setModalOpen(false)}
        onSave={(input) => {
          createPatient(input);
          setRefreshKey((k) => k + 1);
        }}
      />
    </div>
  );
}
